import { normalizeIngredientName } from '../ingredients/normalize-name.js';
import { formatQuantityAmount } from '../quantity/display.js';
import { tokenSimilarity } from '../text/normalize.js';
function ingredientText(i) {
    const amount = formatQuantityAmount({
        quantity: i.quantity ?? null,
        quantityMax: i.quantityMax ?? null,
        quantityText: i.quantityText ?? null,
        unitCode: i.unitCode ?? null,
        unitText: i.unitText ?? null,
    }, 'en');
    const base = amount ? `${amount} ${i.name}` : i.name;
    return i.note ? `${base}, ${i.note}` : base;
}
function flatIngredients(input) {
    return input.ingredientSections.flatMap((s) => s.ingredients);
}
function flatSteps(input) {
    return input.stepSections.flatMap((s) => s.steps);
}
const SCALAR_FIELDS = [
    ['title', 'Title'],
    ['description', 'Description'],
    ['servings', 'Servings'],
    ['servingsLabel', 'Servings label'],
    ['prepTimeMinutes', 'Preparation time'],
    ['cookTimeMinutes', 'Cooking time'],
    ['restTimeMinutes', 'Resting time'],
    ['difficulty', 'Difficulty'],
    ['cuisine', 'Cuisine'],
    ['mealType', 'Meal type'],
    ['notes', 'Notes'],
    ['status', 'Status'],
    ['visibility', 'Visibility'],
];
/**
 * Structured diff between two recipe write inputs. Ingredients are matched by
 * normalized name; steps by best text similarity in order. Used for the AI
 * "material change" confirmation and for revision comparison.
 */
export function diffRecipes(before, after) {
    const changes = [];
    for (const [field, label] of SCALAR_FIELDS) {
        const a = before[field] ?? null;
        const b = after[field] ?? null;
        if (JSON.stringify(a) !== JSON.stringify(b)) {
            changes.push({
                path: field,
                kind: a === null ? 'added' : b === null ? 'removed' : 'changed',
                before: a,
                after: b,
                label,
            });
        }
    }
    const tagsBefore = new Set(before.tags.map((t) => t.toLowerCase()));
    const tagsAfter = new Set(after.tags.map((t) => t.toLowerCase()));
    for (const t of tagsAfter)
        if (!tagsBefore.has(t))
            changes.push({ path: 'tags', kind: 'added', after: t, label: 'Tag' });
    for (const t of tagsBefore)
        if (!tagsAfter.has(t))
            changes.push({ path: 'tags', kind: 'removed', before: t, label: 'Tag' });
    // Ingredients
    const beforeIngredients = flatIngredients(before);
    const afterIngredients = flatIngredients(after);
    const remaining = new Map();
    for (const i of beforeIngredients) {
        const key = normalizeIngredientName(i.name);
        remaining.set(key, [...(remaining.get(key) ?? []), i]);
    }
    const ingredientsAdded = [];
    const ingredientsChanged = [];
    for (const i of afterIngredients) {
        const key = normalizeIngredientName(i.name);
        const bucket = remaining.get(key);
        if (bucket && bucket.length) {
            const prev = bucket.shift();
            const pt = ingredientText(prev);
            const nt = ingredientText(i);
            if (pt !== nt || prev.optional !== i.optional) {
                ingredientsChanged.push({ before: pt, after: nt });
                changes.push({
                    path: 'ingredients',
                    kind: 'changed',
                    before: pt,
                    after: nt,
                    label: 'Ingredient',
                });
            }
        }
        else {
            const text = ingredientText(i);
            ingredientsAdded.push(text);
            changes.push({ path: 'ingredients', kind: 'added', after: text, label: 'Ingredient' });
        }
    }
    const ingredientsRemoved = [];
    for (const bucket of remaining.values()) {
        for (const prev of bucket) {
            const text = ingredientText(prev);
            ingredientsRemoved.push(text);
            changes.push({ path: 'ingredients', kind: 'removed', before: text, label: 'Ingredient' });
        }
    }
    // Steps
    const beforeSteps = flatSteps(before).map((s) => s.instruction);
    const afterSteps = flatSteps(after).map((s) => s.instruction);
    const used = new Set();
    const stepsAdded = [];
    const stepsChanged = [];
    for (const step of afterSteps) {
        let bestIdx = -1;
        let bestScore = 0;
        beforeSteps.forEach((prev, idx) => {
            if (used.has(idx))
                return;
            const score = tokenSimilarity(prev, step);
            if (score > bestScore) {
                bestScore = score;
                bestIdx = idx;
            }
        });
        if (bestIdx >= 0 && bestScore >= 0.5) {
            used.add(bestIdx);
            const prev = beforeSteps[bestIdx];
            if (prev !== step) {
                stepsChanged.push({ before: prev, after: step });
                changes.push({ path: 'steps', kind: 'changed', before: prev, after: step, label: 'Step' });
            }
        }
        else {
            stepsAdded.push(step);
            changes.push({ path: 'steps', kind: 'added', after: step, label: 'Step' });
        }
    }
    const stepsRemoved = [];
    beforeSteps.forEach((prev, idx) => {
        if (!used.has(idx)) {
            stepsRemoved.push(prev);
            changes.push({ path: 'steps', kind: 'removed', before: prev, label: 'Step' });
        }
    });
    return {
        changes,
        ingredientsAdded,
        ingredientsRemoved,
        ingredientsChanged,
        stepsAdded,
        stepsRemoved,
        stepsChanged,
    };
}
export function isMaterialChange(diff) {
    return diff.changes.some((c) => c.path === 'ingredients' || c.path === 'steps' || c.path === 'servings' || c.path === 'title');
}
//# sourceMappingURL=diff.js.map